import { useAppContext } from '../../../context/AppContext';
import { fetchData, postData } from '../../../services';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UsersTable } from '../../tables/UsersTable';
import { CompanyDetails } from '../../blocks/CompanyDetails';
import { ProfileSettings } from '../../blocks/ProfileSettings';
import { CreateCompanyForm, EditCompanyForm, AddUserForm } from '../../forms';
import { ProjectsTable } from '../../tables/ProjectsTable';
import { DesignsTable } from '../../tables/DesignsTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
const ADMIN_SECTIONS = {
    company: 'company',
    users: 'users',
    user: 'user',
    emailLogs: 'email-logs',
    taskLogs: 'task-logs',
    profile: 'profile',
};

function formatDateTime(value) {
    if (value == null || value === '') return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}

export const SuperAdminPage = () => {
    const { token, base_url, user, projects } = useAppContext();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [users, setUsers] = useState([]);
    const [myCompanyUsers, setMyCompanyUsers] = useState([]);
    const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [isLoadingMyUsers, setIsLoadingMyUsers] = useState(false);
    const [adminSection, setAdminSection] = useState(ADMIN_SECTIONS.company);
    const [companyTab, setCompanyTab] = useState('details');
    const [showAddUserForm, setShowAddUserForm] = useState(false);
    const [showAddMyUserForm, setShowAddMyUserForm] = useState(false);
    const [emailSearchValue, setEmailSearchValue] = useState('');
    const [isSearchingByEmail, setIsSearchingByEmail] = useState(false);
    const [searchedByEmail, setSearchedByEmail] = useState(false);
    const [companyDesigns, setCompanyDesigns] = useState([]);
    const [isLoadingDesigns, setIsLoadingDesigns] = useState(false);

    const [emailLogs, setEmailLogs] = useState([]);
    const [taskLogs, setTaskLogs] = useState([]);
    const [logsLoaded, setLogsLoaded] = useState(false);
    const [isLoadingLogs, setIsLoadingLogs] = useState(false);

    const [emailSearchTerm, setEmailSearchTerm] = useState('');
    const [emailTypeFilter, setEmailTypeFilter] = useState('all');
    const [emailSortBy, setEmailSortBy] = useState('sent_desc');

    const [taskSearchTerm, setTaskSearchTerm] = useState('');
    const [taskEventFilter, setTaskEventFilter] = useState('all');
    const [taskSortBy, setTaskSortBy] = useState('logged_desc');
    const appliedUrlCompanyIdRef = useRef(null);

    useEffect(() => {
        const loadCompanies = async () => {
            if (token && user?.is_super_admin) {
                try {
                    setIsLoadingCompanies(true);
                    const response = await fetchData(token, `${base_url}/apis/admin/companies`);
                    if (response && response.companies) {
                        const sortedCompanies = [...response.companies].sort((a, b) => {
                            const nameA = (a.company_name || '').toLowerCase();
                            const nameB = (b.company_name || '').toLowerCase();
                            return nameA.localeCompare(nameB);
                        });
                        setCompanies(sortedCompanies);
                    } else {
                        window.showAlert(500, 'Failed to load companies');
                    }
                } catch (err) {
                    window.showAlert(err.status || 500, err?.msg || 'Error loading companies');
                    console.error('Error loading companies:', err);
                } finally {
                    setIsLoadingCompanies(false);
                }
            }
        };

        loadCompanies();
    }, [token, base_url, user]);

    useEffect(() => {
        const section = searchParams.get('section');
        if (
            section === ADMIN_SECTIONS.profile ||
            section === ADMIN_SECTIONS.users ||
            section === ADMIN_SECTIONS.emailLogs ||
            section === ADMIN_SECTIONS.taskLogs
        ) {
            setAdminSection(section);
        }
    }, [searchParams]);

    useEffect(() => {
        if (!token || !user?.is_super_admin) return;

        const loadMyCompanyUsers = async () => {
            try {
                setIsLoadingMyUsers(true);
                const response = await fetchData(token, `${base_url}/apis/admin/users`);
                if (response && response.users) {
                    setMyCompanyUsers(response.users);
                } else {
                    window.showAlert(500, 'Failed to load users');
                }
            } catch (err) {
                window.showAlert(err.status || 500, err?.msg || 'Error loading users');
                console.error('Error loading company users:', err);
            } finally {
                setIsLoadingMyUsers(false);
            }
        };

        loadMyCompanyUsers();
    }, [token, base_url, user]);

    useEffect(() => {
        if (isLoadingCompanies || companies.length === 0) {
            return;
        }

        const companyIdParam = searchParams.get('companyId');
        if (!companyIdParam) {
            if (appliedUrlCompanyIdRef.current != null) {
                appliedUrlCompanyIdRef.current = null;
                setSelectedCompany(null);
                setSearchedByEmail(false);
            }
            return;
        }

        const companyId = parseInt(companyIdParam, 10);
        if (!Number.isFinite(companyId)) {
            return;
        }

        if (appliedUrlCompanyIdRef.current === companyId) {
            return;
        }

        const company = companies.find((c) => c.id === companyId);
        if (company) {
            appliedUrlCompanyIdRef.current = companyId;
            setSearchedByEmail(false);
            setSelectedCompany(company);
            setAdminSection(ADMIN_SECTIONS.company);
            const tabParam = searchParams.get('tab');
            setCompanyTab(
                tabParam === 'users' || tabParam === 'projects' || tabParam === 'designs'
                    ? tabParam
                    : 'details'
            );
        }
    }, [searchParams, companies, isLoadingCompanies]);

    useEffect(() => {
        if (searchedByEmail) return;

        const loadUsers = async () => {
            if (token && selectedCompany && user?.is_super_admin) {
                try {
                    setIsLoadingUsers(true);
                    const response = await fetchData(token, `${base_url}/apis/admin/users?company_id=${selectedCompany.id}`);
                    if (response && response.users) {
                        setUsers(response.users);
                    } else {
                        window.showAlert(500, 'Failed to load users');
                        setUsers([]);
                    }
                } catch (err) {
                    window.showAlert(err.status || 500, err?.msg || 'Error loading users');
                    console.error('Error loading users:', err);
                    setUsers([]);
                } finally {
                    setIsLoadingUsers(false);
                }
            }
        };

        if (selectedCompany) {
            loadUsers();
        } else {
            setUsers([]);
        }
    }, [token, base_url, selectedCompany, user, searchedByEmail]);

    const companyProjects = useMemo(() => {
        if (!selectedCompany) return [];
        return projects.filter(project => project.company_id === selectedCompany.id);
    }, [selectedCompany, projects]);

    useEffect(() => {
        setCompanyDesigns([]);
    }, [selectedCompany?.id]);

    useEffect(() => {
        if (companyTab !== 'designs' || adminSection !== ADMIN_SECTIONS.company) {
            return;
        }

        if (!token || !selectedCompany || !user?.is_super_admin) {
            setCompanyDesigns([]);
            return;
        }

        if (companyProjects.length === 0) {
            setCompanyDesigns([]);
            return;
        }

        let cancelled = false;

        const loadCompanyDesigns = async () => {
            try {
                setIsLoadingDesigns(true);
                const results = await Promise.all(
                    companyProjects.map(async (project) => {
                        try {
                            const response = await fetchData(token, `${base_url}/apis/designs/${project.id}`);
                            const designsList = response?.designs || [];
                            const projectName =
                                project.project_name ||
                                project.name ||
                                project.title ||
                                `Project #${project.id}`;
                            return designsList.map((design) => ({
                                ...design,
                                project_id: project.id,
                                project_name: projectName,
                            }));
                        } catch (err) {
                            console.error(`Error loading designs for project ${project.id}:`, err);
                            return [];
                        }
                    })
                );

                if (cancelled) return;

                const allDesigns = results
                    .flat()
                    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
                setCompanyDesigns(allDesigns);
            } finally {
                if (!cancelled) setIsLoadingDesigns(false);
            }
        };

        loadCompanyDesigns();

        return () => {
            cancelled = true;
        };
    }, [companyTab, adminSection, token, base_url, selectedCompany, companyProjects, user]);

    useEffect(() => {
        const needsLogs =
            adminSection === ADMIN_SECTIONS.emailLogs ||
            adminSection === ADMIN_SECTIONS.taskLogs;
        if (!needsLogs || logsLoaded || !token || user?.is_super_admin !== true) {
            return;
        }

        let cancelled = false;

        const loadLogs = async () => {
            try {
                setIsLoadingLogs(true);
                const response = await fetchData(token, `${base_url}/apis/admin/get-logs`);
                if (cancelled) return;
                const email =
                    response?.data?.email_logs ??
                    response?.email_logs ??
                    [];
                const tasks =
                    response?.data?.task_logs ??
                    response?.task_logs ??
                    [];
                setEmailLogs(Array.isArray(email) ? email : []);
                setTaskLogs(Array.isArray(tasks) ? tasks : []);
                setLogsLoaded(true);
            } catch (err) {
                if (!cancelled) {
                    window.showAlert(err.status || 500, err?.msg || 'Error loading audit logs');
                    console.error('Error loading audit logs:', err);
                    setEmailLogs([]);
                    setTaskLogs([]);
                }
            } finally {
                if (!cancelled) setIsLoadingLogs(false);
            }
        };

        loadLogs();

        return () => {
            cancelled = true;
        };
    }, [adminSection, logsLoaded, token, base_url, user?.is_super_admin]);

    const uniqueEmailMessageTypes = useMemo(() => {
        const types = new Set();
        emailLogs.forEach((row) => {
            if (row.message_type) types.add(row.message_type);
        });
        return Array.from(types).sort();
    }, [emailLogs]);

    const uniqueTaskEventTypes = useMemo(() => {
        const types = new Set();
        taskLogs.forEach((row) => {
            if (row.event_type) types.add(row.event_type);
        });
        return Array.from(types).sort();
    }, [taskLogs]);

    const filteredEmailLogs = useMemo(() => {
        let list = [...emailLogs];
        if (emailSearchTerm) {
            const q = emailSearchTerm.toLowerCase();
            list = list.filter((row) => {
                const hay = [
                    row.message_type,
                    row.subject,
                    row.recipients,
                    row.project_site_id,
                    row.id,
                ]
                    .map((x) => (x != null ? String(x).toLowerCase() : ''))
                    .join(' ');
                return hay.includes(q);
            });
        }
        if (emailTypeFilter !== 'all') {
            list = list.filter((row) => row.message_type === emailTypeFilter);
        }
        list.sort((a, b) => {
            switch (emailSortBy) {
                case 'sent_asc': {
                    const ta = new Date(a.sent_at || 0).getTime();
                    const tb = new Date(b.sent_at || 0).getTime();
                    return ta - tb;
                }
                case 'sent_desc': {
                    const ta = new Date(a.sent_at || 0).getTime();
                    const tb = new Date(b.sent_at || 0).getTime();
                    return tb - ta;
                }
                case 'id_asc':
                    return (a.id || 0) - (b.id || 0);
                case 'id_desc':
                    return (b.id || 0) - (a.id || 0);
                case 'message_type': {
                    const ma = (a.message_type || '').toLowerCase();
                    const mb = (b.message_type || '').toLowerCase();
                    return ma.localeCompare(mb);
                }
                default:
                    return 0;
            }
        });
        return list;
    }, [emailLogs, emailSearchTerm, emailTypeFilter, emailSortBy]);

    const filteredTaskLogs = useMemo(() => {
        let list = [...taskLogs];
        if (taskSearchTerm) {
            const q = taskSearchTerm.toLowerCase();
            list = list.filter((row) => {
                const hay = [
                    row.event_type,
                    row.task_name,
                    row.changed_by,
                    row.changes_summary,
                    row.project_site_id,
                    row.design_id,
                    row.id,
                ]
                    .map((x) => (x != null ? String(x).toLowerCase() : ''))
                    .join(' ');
                return hay.includes(q);
            });
        }
        if (taskEventFilter !== 'all') {
            list = list.filter((row) => row.event_type === taskEventFilter);
        }
        list.sort((a, b) => {
            switch (taskSortBy) {
                case 'logged_asc': {
                    const ta = new Date(a.logged_at || 0).getTime();
                    const tb = new Date(b.logged_at || 0).getTime();
                    return ta - tb;
                }
                case 'logged_desc': {
                    const ta = new Date(a.logged_at || 0).getTime();
                    const tb = new Date(b.logged_at || 0).getTime();
                    return tb - ta;
                }
                case 'id_asc':
                    return (a.id || 0) - (b.id || 0);
                case 'id_desc':
                    return (b.id || 0) - (a.id || 0);
                case 'event_type': {
                    const ea = (a.event_type || '').toLowerCase();
                    const eb = (b.event_type || '').toLowerCase();
                    return ea.localeCompare(eb);
                }
                default:
                    return 0;
            }
        });
        return list;
    }, [taskLogs, taskSearchTerm, taskEventFilter, taskSortBy]);

    if (!user?.is_super_admin) {
        return (
            <div className="admin-empty-state">
                <h2>Access Denied</h2>
                <p>You don&apos;t have permission to access this page.</p>
            </div>
        );
    }

    const setCompanyIdParam = (companyId) => {
        const nextValue = companyId != null && companyId !== '' ? String(companyId) : '';
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            const current = next.get('companyId');
            if (current === nextValue || (!current && !nextValue)) {
                return prev;
            }
            if (nextValue) {
                next.set('companyId', nextValue);
            } else {
                next.delete('companyId');
            }
            return next;
        });
    };

    const handleCompanyChange = (e) => {
        setSearchedByEmail(false);
        const companyId = e.target.value;
        if (companyId) {
            const parsedId = parseInt(companyId, 10);
            const company = companies.find(c => c.id === parsedId);
            setSelectedCompany(company);
            setCompanyTab('details');
            appliedUrlCompanyIdRef.current = parsedId;
            setCompanyIdParam(companyId);
        } else {
            setSelectedCompany(null);
            appliedUrlCompanyIdRef.current = null;
            setCompanyIdParam(null);
        }
    };

    const handleEmailSearch = async (e) => {
        e.preventDefault();
        const email = (emailSearchValue || '').trim();
        if (!email) return;
        if (!token || !user?.is_super_admin) return;
        try {
            setIsSearchingByEmail(true);
            const response = await postData(token, `${base_url}/apis/admin/users-by-email`, { email });
            if (!response) {
                window.showAlert(500, 'No response from server');
                return;
            }
            const { users: emailUsers, company_id } = response;
            const company = companies.find(c => c.id === company_id);
            if (!company) {
                window.showAlert(404, 'Company not found for this email');
                return;
            }
            setSelectedCompany(company);
            setUsers(Array.isArray(emailUsers) ? emailUsers : []);
            setSearchedByEmail(true);
        } catch (err) {
            window.showAlert(err.status || 500, err?.msg || 'Error searching by email');
            console.error('Error searching by email:', err);
        } finally {
            setIsSearchingByEmail(false);
        }
    };

    const handleCompanyCreated = (newCompany) => {
        setCompanies(prev => {
            const updated = [...prev, newCompany];
            return updated.sort((a, b) => {
                const nameA = (a.company_name || '').toLowerCase();
                const nameB = (b.company_name || '').toLowerCase();
                return nameA.localeCompare(nameB);
            });
        });
        setSelectedCompany(newCompany);
        setAdminSection(ADMIN_SECTIONS.company);
        setCompanyTab('details');
        appliedUrlCompanyIdRef.current = newCompany.id;
        setCompanyIdParam(newCompany.id);
    };

    const handleCompanyUpdated = (updatedCompany) => {
        setCompanies(prev => {
            const updated = prev.map(c => c.id === updatedCompany.id ? updatedCompany : c);
            return updated.sort((a, b) => {
                const nameA = (a.company_name || '').toLowerCase();
                const nameB = (b.company_name || '').toLowerCase();
                return nameA.localeCompare(nameB);
            });
        });
        setSelectedCompany(updatedCompany);
    };

    const openCompanyFromUserSearch = () => {
        setSearchedByEmail(false);
        setAdminSection(ADMIN_SECTIONS.company);
        setCompanyTab('details');
        if (selectedCompany?.id) {
            appliedUrlCompanyIdRef.current = selectedCompany.id;
            setCompanyIdParam(selectedCompany.id);
        }
    };

    const renderEmailLogsSection = () => {
        if (isLoadingLogs) {
            return (
                <div className="loading-panel admin-loading-panel">
                    Loading email logs...
                </div>
            );
        }

        return (
            <>
                {emailLogs.length > 0 && (
                    <div className="admin-tools admin-tools--compact">
                        <h3>Search &amp; filter</h3>
                        <div className="admin-tools-grid">
                            <div className="form-group">
                                <label htmlFor="email-audit-search" className="form-label">Search</label>
                                <input
                                    id="email-audit-search"
                                    type="text"
                                    className="form-input"
                                    placeholder="Type, subject, recipients, project ID…"
                                    value={emailSearchTerm}
                                    onChange={(e) => setEmailSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="email-audit-type" className="form-label">Message type</label>
                                <select
                                    id="email-audit-type"
                                    className="form-select"
                                    value={emailTypeFilter}
                                    onChange={(e) => setEmailTypeFilter(e.target.value)}
                                >
                                    <option value="all">All types</option>
                                    {uniqueEmailMessageTypes.map((t) => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor="email-audit-sort" className="form-label">Sort by</label>
                                <select
                                    id="email-audit-sort"
                                    className="form-select"
                                    value={emailSortBy}
                                    onChange={(e) => setEmailSortBy(e.target.value)}
                                >
                                    <option value="sent_desc">Sent at (newest first)</option>
                                    <option value="sent_asc">Sent at (oldest first)</option>
                                    <option value="id_asc">Log ID (low to high)</option>
                                    <option value="id_desc">Log ID (high to low)</option>
                                    <option value="message_type">Message type (A–Z)</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <button
                                    type="button"
                                    className="btn btn-secondary btn--block"
                                    onClick={() => {
                                        setEmailSearchTerm('');
                                        setEmailTypeFilter('all');
                                        setEmailSortBy('sent_desc');
                                    }}
                                >
                                    Clear filters
                                </button>
                            </div>
                        </div>
                        <p className="admin-tools-summary admin-tools-summary--bare" aria-live="polite">
                            Showing {filteredEmailLogs.length} of {emailLogs.length} email logs
                            {emailSearchTerm && ` (search: "${emailSearchTerm}")`}
                            {emailTypeFilter !== 'all' && ` (type: ${emailTypeFilter})`}
                        </p>
                    </div>
                )}
                {emailLogs.length === 0 ? (
                    <div className="admin-empty-state">
                        <h3>No email logs</h3>
                        <p>There are no recorded email sends yet.</p>
                    </div>
                ) : (
                    <div className="admin-table-scroll">
                        <table>
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Message type</th>
                                    <th>Subject</th>
                                    <th>Recipients</th>
                                    <th>Project site ID</th>
                                    <th>Sent at</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredEmailLogs.map((row) => (
                                    <tr key={row.id}>
                                        <td>{row.id}</td>
                                        <td>{row.message_type ?? '—'}</td>
                                        <td className="admin-table-cell--email">
                                            {row.subject ?? '—'}
                                        </td>
                                        <td className="admin-table-cell--body">
                                            {row.recipients ?? '—'}
                                        </td>
                                        <td>{row.project_site_id ?? '—'}</td>
                                        <td>{formatDateTime(row.sent_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </>
        );
    };

    const renderTaskLogsSection = () => {
        if (isLoadingLogs) {
            return (
                <div className="loading-panel admin-loading-panel">
                    Loading task logs...
                </div>
            );
        }

        return (
            <>
                {taskLogs.length > 0 && (
                    <div className="admin-tools admin-tools--compact">
                        <h3>Search &amp; filter</h3>
                        <div className="admin-tools-grid">
                            <div className="form-group">
                                <label htmlFor="task-audit-search" className="form-label">Search</label>
                                <input
                                    id="task-audit-search"
                                    type="text"
                                    className="form-input"
                                    placeholder="Event, task, user, project ID, design ID…"
                                    value={taskSearchTerm}
                                    onChange={(e) => setTaskSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="task-audit-event" className="form-label">Event type</label>
                                <select
                                    id="task-audit-event"
                                    className="form-select"
                                    value={taskEventFilter}
                                    onChange={(e) => setTaskEventFilter(e.target.value)}
                                >
                                    <option value="all">All events</option>
                                    {uniqueTaskEventTypes.map((t) => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor="task-audit-sort" className="form-label">Sort by</label>
                                <select
                                    id="task-audit-sort"
                                    className="form-select"
                                    value={taskSortBy}
                                    onChange={(e) => setTaskSortBy(e.target.value)}
                                >
                                    <option value="logged_desc">Logged at (newest first)</option>
                                    <option value="logged_asc">Logged at (oldest first)</option>
                                    <option value="id_asc">Log ID (low to high)</option>
                                    <option value="id_desc">Log ID (high to low)</option>
                                    <option value="event_type">Event type (A–Z)</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <button
                                    type="button"
                                    className="btn btn-secondary btn--block"
                                    onClick={() => {
                                        setTaskSearchTerm('');
                                        setTaskEventFilter('all');
                                        setTaskSortBy('logged_desc');
                                    }}
                                >
                                    Clear filters
                                </button>
                            </div>
                        </div>
                        <p className="admin-tools-summary admin-tools-summary--bare" aria-live="polite">
                            Showing {filteredTaskLogs.length} of {taskLogs.length} task logs
                            {taskSearchTerm && ` (search: "${taskSearchTerm}")`}
                            {taskEventFilter !== 'all' && ` (event: ${taskEventFilter})`}
                        </p>
                    </div>
                )}
                {taskLogs.length === 0 ? (
                    <div className="admin-empty-state">
                        <h3>No task logs</h3>
                        <p>There are no recorded task changes yet.</p>
                    </div>
                ) : (
                    <div className="admin-table-scroll">
                        <table>
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Event type</th>
                                    <th>Task name</th>
                                    <th>Changed by</th>
                                    <th>Changes summary</th>
                                    <th>Project site ID</th>
                                    <th>Design ID</th>
                                    <th>Logged at</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTaskLogs.map((row) => (
                                    <tr key={row.id}>
                                        <td>{row.id}</td>
                                        <td>{row.event_type ?? '—'}</td>
                                        <td className="admin-table-cell--subject">
                                            {row.task_name ?? '—'}
                                        </td>
                                        <td>{row.changed_by ?? '—'}</td>
                                        <td className="admin-table-cell--task-body">
                                            {row.changes_summary ?? '—'}
                                        </td>
                                        <td>{row.project_site_id ?? '—'}</td>
                                        <td>{row.design_id ?? '—'}</td>
                                        <td>{formatDateTime(row.logged_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </>
        );
    };

    return (
        <div className="page">
            <div className="main-content">
                <h1 className="admin-page-title">Super Admin</h1>

                <div className="admin-tabs">
                    <div className="admin-tabs__list">
                        <button
                            type="button"
                            onClick={() => setAdminSection(ADMIN_SECTIONS.company)}
                            className={`admin-tab${adminSection === ADMIN_SECTIONS.company ? ' admin-tab--active' : ''}`}
                        >
                            Find a company
                        </button>
                        <button
                            type="button"
                            onClick={() => setAdminSection(ADMIN_SECTIONS.user)}
                            className={`admin-tab${adminSection === ADMIN_SECTIONS.user ? ' admin-tab--active' : ''}`}
                        >
                            Find a user
                        </button>
                        <button
                            type="button"
                            onClick={() => setAdminSection(ADMIN_SECTIONS.emailLogs)}
                            className={`admin-tab${adminSection === ADMIN_SECTIONS.emailLogs ? ' admin-tab--active' : ''}`}
                        >
                            Email logs
                            {logsLoaded && (
                                <span className={`admin-tab__count${adminSection === ADMIN_SECTIONS.emailLogs ? ' admin-tab__count--active' : ''}`}>
                                    {emailLogs.length}
                                </span>
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={() => setAdminSection(ADMIN_SECTIONS.taskLogs)}
                            className={`admin-tab${adminSection === ADMIN_SECTIONS.taskLogs ? ' admin-tab--active' : ''}`}
                        >
                            Task logs
                            {logsLoaded && (
                                <span className={`admin-tab__count${adminSection === ADMIN_SECTIONS.taskLogs ? ' admin-tab__count--active' : ''}`}>
                                    {taskLogs.length}
                                </span>
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={() => setAdminSection(ADMIN_SECTIONS.users)}
                            className={`admin-tab${adminSection === ADMIN_SECTIONS.users ? ' admin-tab--active' : ''}`}
                        >
                            Users
                            <span className={`admin-tab__count${adminSection === ADMIN_SECTIONS.users ? ' admin-tab__count--active' : ''}`}>
                                {myCompanyUsers.length}
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setAdminSection(ADMIN_SECTIONS.profile)}
                            className={`admin-tab${adminSection === ADMIN_SECTIONS.profile ? ' admin-tab--active' : ''}`}
                        >
                            My Profile
                        </button>
                    </div>
                </div>

                {adminSection === ADMIN_SECTIONS.company && (
                    <>
                        <div className="admin-company-select">
                            <label htmlFor="company-select" className="admin-toolbar__label">
                                Select company
                            </label>
                            <div className="admin-toolbar__row">
                                <select
                                    id="company-select"
                                    className="form-select"
                                    value={selectedCompany?.id || ''}
                                    onChange={handleCompanyChange}
                                    disabled={isLoadingCompanies}
                                >
                                    <option value="">
                                        {isLoadingCompanies ? 'Loading companies...' : 'Select a company...'}
                                    </option>
                                    {companies.map((company) => (
                                        <option key={company.id} value={company.id}>
                                            {company.company_name}
                                        </option>
                                    ))}
                                </select>
                                <CreateCompanyForm onCompanyCreated={handleCompanyCreated} />
                            </div>
                        </div>

                        {selectedCompany && (
                            <div>
                                <div className="admin-tabs">
                                    <div className="admin-tabs__list admin-tabs__list--wide-gap">
                                        {[
                                            { id: 'details', label: 'Company details' },
                                            { id: 'users', label: 'Users', count: users.length },
                                            { id: 'projects', label: 'Projects', count: companyProjects.length },
                                            { id: 'designs', label: 'Designs', count: companyDesigns.length },
                                        ].map((tab) => (
                                            <button
                                                key={tab.id}
                                                type="button"
                                                onClick={() => setCompanyTab(tab.id)}
                                                className={`admin-tab${companyTab === tab.id ? ' admin-tab--active' : ''}`}
                                            >
                                                {tab.label}
                                                {tab.count != null && (
                                                    <span className={`admin-tab__count${companyTab === tab.id ? ' admin-tab__count--active' : ''}`}>
                                                        {tab.count}
                                                    </span>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {companyTab === 'details' && (
                                    <div>
                                        <div className="admin-section-header">
                                            <h2>Company Details</h2>
                                            <EditCompanyForm
                                                company={selectedCompany}
                                                onCompanyUpdated={handleCompanyUpdated}
                                            />
                                        </div>
                                        <CompanyDetails company={selectedCompany} showEditButton={false} />
                                    </div>
                                )}

                                {companyTab === 'users' && (
                                    <div>
                                        <div className="admin-section-header">
                                            <h2>Company Users</h2>
                                            <div className="admin-section-header__meta">
                                                <div className="admin-section-header__count">
                                                    {users.length} user{users.length !== 1 ? 's' : ''} total
                                                </div>
                                                <button
                                                    className="btn btn-primary admin-btn-add"
                                                    onClick={() => setShowAddUserForm(true)}
                                                >
                                                    <FontAwesomeIcon icon={faPlus} className="icon" />
                                                    Add User
                                                </button>
                                            </div>
                                        </div>
                                        <UsersTable
                                            users={users}
                                            setUsers={setUsers}
                                            isLoading={isLoadingUsers}
                                            companyOverride={selectedCompany}
                                        />
                                        {showAddUserForm && (
                                            <AddUserForm
                                                setUsers={setUsers}
                                                onClose={() => setShowAddUserForm(false)}
                                                companyId={selectedCompany?.id}
                                            />
                                        )}
                                    </div>
                                )}

                                {companyTab === 'projects' && (
                                    <div>
                                        <div className="admin-section-header">
                                            <h2>Company Projects</h2>
                                            <div className="admin-section-header__meta">
                                                <div className="admin-section-header__count">
                                                    {companyProjects.length} project{companyProjects.length !== 1 ? 's' : ''} total
                                                </div>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary admin-btn-add"
                                                    onClick={() => {
                                                        const params = new URLSearchParams({
                                                            companyId: String(selectedCompany.id),
                                                        });
                                                        if (selectedCompany.company_name) {
                                                            params.set('companyName', selectedCompany.company_name);
                                                        }
                                                        navigate(`/new-project?${params.toString()}`);
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faPlus} className="icon" />
                                                    New Project
                                                </button>
                                            </div>
                                        </div>
                                        {companyProjects.length > 0 ? (
                                            <ProjectsTable projects={companyProjects} />
                                        ) : (
                                            <div className="admin-empty-state">
                                                <h3>No Projects Found</h3>
                                                <p>This company doesn&apos;t have any projects yet.</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {companyTab === 'designs' && (
                                    <div>
                                        <div className="admin-designs-toolbar">
                                            <h2>Company Designs</h2>
                                            <div className="admin-section-header__count">
                                                {isLoadingDesigns
                                                    ? 'Loading designs...'
                                                    : `${companyDesigns.length} design${companyDesigns.length !== 1 ? 's' : ''} total`}
                                            </div>
                                        </div>
                                        {isLoadingDesigns ? (
                                            <div className="loading-panel admin-loading-panel">
                                                Loading designs...
                                            </div>
                                        ) : companyDesigns.length > 0 ? (
                                            <DesignsTable variant="company" designs={companyDesigns} />
                                        ) : (
                                            <div className="admin-empty-state">
                                                <h3>No Designs Found</h3>
                                                <p>
                                                    {companyProjects.length > 0
                                                        ? "This company's projects don't have any designs yet."
                                                        : "This company doesn't have any projects yet."}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {!selectedCompany && !isLoadingCompanies && (
                            <div className="admin-empty-state">
                                <h3>Select a company</h3>
                                <p>Choose a company from the dropdown to view details, users, projects, and designs.</p>
                            </div>
                        )}
                    </>
                )}

                {adminSection === ADMIN_SECTIONS.users && (
                    <div>
                        <div className="admin-section-header">
                            <h2>Manage Users</h2>
                            <div className="admin-section-header__meta">
                                <div className="admin-section-header__count">
                                    {myCompanyUsers.length} user{myCompanyUsers.length !== 1 ? 's' : ''} total
                                </div>
                                <button
                                    className="btn btn-primary admin-btn-add"
                                    onClick={() => setShowAddMyUserForm(true)}
                                >
                                    <FontAwesomeIcon icon={faPlus} className="icon" />
                                    Add User
                                </button>
                            </div>
                        </div>

                        <UsersTable
                            users={myCompanyUsers}
                            setUsers={setMyCompanyUsers}
                            isLoading={isLoadingMyUsers}
                        />

                        {showAddMyUserForm && (
                            <AddUserForm
                                setUsers={setMyCompanyUsers}
                                onClose={() => setShowAddMyUserForm(false)}
                            />
                        )}
                    </div>
                )}

                {adminSection === ADMIN_SECTIONS.user && (
                    <>
                        <form onSubmit={handleEmailSearch} className="admin-search-form">
                            <label htmlFor="email-search" className="admin-toolbar__label">
                                Search by email
                            </label>
                            <div className="admin-toolbar__row admin-toolbar__row--compact">
                                <input
                                    id="email-search"
                                    type="email"
                                    value={emailSearchValue}
                                    onChange={(e) => setEmailSearchValue(e.target.value)}
                                    placeholder="Enter email address"
                                    disabled={isSearchingByEmail}
                                    className="form-input admin-toolbar__input"
                                />
                                <button
                                    type="submit"
                                    className="btn btn-primary admin-toolbar__submit"
                                    disabled={isSearchingByEmail || !emailSearchValue.trim()}
                                >
                                    {isSearchingByEmail ? 'Searching...' : 'Search'}
                                </button>
                            </div>
                        </form>

                        {searchedByEmail && selectedCompany && users.length > 0 && (
                            <div>
                                <div className="admin-section-header">
                                    <div>
                                        <h2>Search results</h2>
                                        <p className="admin-search-results__subtitle">
                                            Company: {selectedCompany.company_name}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={openCompanyFromUserSearch}
                                    >
                                        View full company
                                    </button>
                                </div>
                                <UsersTable
                                    users={users}
                                    setUsers={setUsers}
                                    isLoading={isSearchingByEmail}
                                    companyOverride={selectedCompany}
                                />
                            </div>
                        )}

                        {searchedByEmail && selectedCompany && users.length === 0 && (
                            <div className="admin-empty-state">
                                <h3>No users found</h3>
                                <p>No users matched that email address.</p>
                            </div>
                        )}

                        {!searchedByEmail && (
                            <div className="admin-empty-state">
                                <h3>Find a user</h3>
                                <p>Enter an email address to locate the user and their company.</p>
                            </div>
                        )}
                    </>
                )}

                {adminSection === ADMIN_SECTIONS.emailLogs && renderEmailLogsSection()}
                {adminSection === ADMIN_SECTIONS.taskLogs && renderTaskLogsSection()}

                {adminSection === ADMIN_SECTIONS.profile && <ProfileSettings />}
            </div>
        </div>
    );
};
